import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, X, CheckCircle, Upload, GripVertical, Image as ImageIcon, DollarSign, Percent } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COST_TYPES = [
  { value: "carburant", label: "Carburant" },
  { value: "chauffeur", label: "Chauffeur" },
  { value: "decoration", label: "Décoration" },
  { value: "entretien", label: "Entretien" },
  { value: "assurance", label: "Assurance" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "autre", label: "Autre" },
];

export default function AdminVehicules() {
  const { authHeaders } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editVehicle, setEditVehicle] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchVehicles = () => {
    setLoading(true);
    axios.get(`${API}/vehicles`).then(r => setVehicles(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchVehicles(); }, []);

  const deleteVehicle = async (id) => {
    await axios.delete(`${API}/vehicles/${id}`, { headers: authHeaders });
    setDeleting(null);
    fetchVehicles();
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="vehicules-title">Véhicules</h1>
          <p className="text-gray-500 text-sm mt-1">Gérez votre flotte de véhicules</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-vehicle-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors">
          <Plus size={16} /> AJOUTER UN VÉHICULE
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {vehicles.map(v => (
            <div key={v.id} className="bg-white shadow-sm overflow-hidden" data-testid={`vehicle-card-admin-${v.id}`}>
              <div className="h-48 overflow-hidden">
                <img src={v.images?.[0]} alt={v.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-xs font-semibold tracking-widest text-[#D4A843] uppercase">{v.brand}</p>
                    <h3 className="text-xl font-bold text-gray-900">{v.name}</h3>
                  </div>
                  <span className="text-xl font-bold text-[#D4A843]">{v.price}€</span>
                </div>
                <p className="text-gray-500 text-sm mb-4 line-clamp-2">{v.description}</p>
                <ul className="space-y-1 mb-5">
                  {v.features?.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-500">
                      <CheckCircle size={11} className="text-[#D4A843]" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <button onClick={() => setEditVehicle(v)} data-testid={`edit-vehicle-${v.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border-2 border-gray-200 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all">
                    <Pencil size={14} /> Modifier
                  </button>
                  <button onClick={() => setDeleting(v.id)} data-testid={`delete-vehicle-${v.id}`}
                    className="px-4 py-2.5 border-2 border-red-100 text-red-400 hover:bg-red-50 hover:border-red-200 transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showAdd || editVehicle) && (
        <VehicleModal
          vehicle={editVehicle}
          authHeaders={authHeaders}
          onClose={() => { setShowAdd(false); setEditVehicle(null); }}
          onSaved={() => { setShowAdd(false); setEditVehicle(null); fetchVehicles(); }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 mb-3">Supprimer ce véhicule ?</h3>
            <p className="text-gray-500 text-sm mb-6">Cette action est irréversible.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="px-5 py-2 border border-gray-200 text-sm font-semibold">Annuler</button>
              <button onClick={() => deleteVehicle(deleting)} data-testid="confirm-delete-vehicle"
                className="px-5 py-2 bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VehicleModal({ vehicle, authHeaders, onClose, onSaved }) {
  const [form, setForm] = useState({
    brand: vehicle?.brand || "",
    name: vehicle?.name || "",
    price: vehicle?.price || "",
    description: vehicle?.description || "",
    features: vehicle?.features?.join("\n") || "",
    is_available: vehicle?.is_available ?? true,
    ignorer_suivi: vehicle?.ignorer_suivi ?? false,
  });
  const [costs, setCosts] = useState(vehicle?.costs || []);
  const [newCost, setNewCost] = useState({ type: "carburant", label: "", amount: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("general");

  // Calculate totals
  const totalCosts = costs.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const margin = (parseFloat(form.price) || 0) - totalCosts;
  const marginRate = form.price > 0 ? Math.round((margin / form.price) * 100) : 0;

  // Load existing images if editing
  useEffect(() => {
    if (vehicle?.id) {
      axios.get(`${API}/vehicles/${vehicle.id}/images`, { headers: authHeaders })
        .then(r => setImages(r.data))
        .catch(() => {});
    }
  }, [vehicle?.id, authHeaders]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addCost = () => {
    if (!newCost.label || !newCost.amount) return;
    setCosts([...costs, { 
      id: `temp-${Date.now()}`,
      type: newCost.type, 
      label: newCost.label, 
      amount: parseFloat(newCost.amount) 
    }]);
    setNewCost({ type: "carburant", label: "", amount: "" });
  };

  const removeCost = (index) => {
    setCosts(costs.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !vehicle?.id) return;
    
    setUploading(true);
    setError("");
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/vehicles/${vehicle.id}/images`, formData, {
          headers: { ...authHeaders, "Content-Type": "multipart/form-data" }
        });
        setImages(prev => [...prev, res.data]);
      } catch (err) {
        setError(err.response?.data?.detail || `Erreur upload ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const deleteImage = async (imageId) => {
    if (!vehicle?.id) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicle.id}/images/${imageId}`, { headers: authHeaders });
      setImages(prev => prev.filter(img => img.id !== imageId));
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur suppression");
    }
  };

  const moveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    setImages(newImages);
    // Save order to backend
    if (vehicle?.id) {
      axios.put(`${API}/vehicles/${vehicle.id}/images/reorder`, 
        newImages.map(img => img.id), 
        { headers: authHeaders }
      ).catch(() => {});
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const data = {
      ...form,
      price: parseInt(form.price),
      features: form.features.split("\n").map(s => s.trim()).filter(Boolean),
      images: images.map(img => img.url), // Use uploaded image URLs
      costs: costs.map(c => ({ type: c.type, label: c.label, amount: parseFloat(c.amount) })),
    };
    try {
      if (vehicle) {
        await axios.put(`${API}/vehicles/${vehicle.id}`, data, { headers: authHeaders });
      } else {
        await axios.post(`${API}/vehicles`, data, { headers: authHeaders });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto rounded">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">{vehicle ? "Modifier le véhicule" : "Ajouter un véhicule"}</h2>
            {vehicle && form.price > 0 && (
              <div className="flex items-center gap-4 mt-1 text-sm">
                <span className="text-gray-500">Prix: <strong className="text-gray-900">{form.price}€</strong></span>
                <span className="text-gray-500">Coûts: <strong className="text-red-500">{totalCosts}€</strong></span>
                <span className={`font-semibold ${margin >= 0 ? "text-green-600" : "text-red-600"}`}>
                  Marge: {margin}€ ({marginRate}%)
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {[
            { key: "general", label: "Général" },
            { key: "costs", label: `Coûts (${costs.length})` },
            { key: "photos", label: `Photos (${images.length})` },
          ].map(tab => (
            <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.key ? "border-[#D4A843] text-[#D4A843]" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded">{error}</p>}

          {/* Tab: General */}
          {activeTab === "general" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                {[["brand", "Marque *"], ["name", "Nom *"]].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
                    <input type="text" value={form[key]} onChange={e => set(key, e.target.value)} required
                      data-testid={`vehicle-${key}`}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] rounded" />
                  </div>
                ))}
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Prix de vente (€) *</label>
                <input type="number" value={form.price} onChange={e => set("price", e.target.value)} required
                  data-testid="vehicle-price"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] rounded" />
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Description *</label>
                <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} required
                  data-testid="vehicle-description"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none rounded" />
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Caractéristiques (une par ligne)</label>
                <textarea value={form.features} onChange={e => set("features", e.target.value)} rows={3}
                  placeholder="Chauffeur professionnel&#10;Décoration véhicule"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none rounded" />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="available" checked={form.is_available}
                  onChange={e => set("is_available", e.target.checked)} className="accent-[#D4A843]" />
                <label htmlFor="available" className="text-sm text-gray-700">Disponible à la location</label>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                <input type="checkbox" id="ignorer_suivi" checked={form.ignorer_suivi}
                  onChange={e => set("ignorer_suivi", e.target.checked)} className="accent-amber-500" />
                <div>
                  <label htmlFor="ignorer_suivi" className="text-sm font-semibold text-amber-800 cursor-pointer">
                    Ignorer le suivi de rentabilité
                  </label>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Ce véhicule ne sera pas compté dans le taux de remplissage ni le CA (ex: sous-traitant)
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Tab: Costs */}
          {activeTab === "costs" && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded text-center">
                  <p className="text-xs text-gray-500 uppercase mb-1">Prix de vente</p>
                  <p className="text-xl font-bold text-gray-900">{form.price || 0}€</p>
                </div>
                <div className="bg-red-50 p-4 rounded text-center">
                  <p className="text-xs text-red-500 uppercase mb-1">Total coûts</p>
                  <p className="text-xl font-bold text-red-600">{totalCosts}€</p>
                </div>
                <div className={`p-4 rounded text-center ${margin >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                  <p className={`text-xs uppercase mb-1 ${margin >= 0 ? "text-green-500" : "text-red-500"}`}>Marge</p>
                  <p className={`text-xl font-bold ${margin >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {margin}€ <span className="text-sm font-medium">({marginRate}%)</span>
                  </p>
                </div>
              </div>

              {/* Add cost form */}
              <div className="border border-gray-200 rounded p-4 bg-gray-50">
                <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider flex items-center gap-2">
                  <Plus size={14} /> Ajouter une charge
                </p>
                <div className="grid grid-cols-12 gap-2">
                  <select value={newCost.type} onChange={e => setNewCost({...newCost, type: e.target.value})}
                    className="col-span-3 border border-gray-200 px-2 py-2 text-sm rounded focus:outline-none focus:border-[#D4A843]">
                    {COST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input type="text" value={newCost.label} onChange={e => setNewCost({...newCost, label: e.target.value})}
                    placeholder="Description de la charge"
                    className="col-span-6 border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-[#D4A843]" />
                  <input type="number" value={newCost.amount} onChange={e => setNewCost({...newCost, amount: e.target.value})}
                    placeholder="€"
                    className="col-span-2 border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-[#D4A843]" />
                  <button type="button" onClick={addCost}
                    className="col-span-1 bg-[#1B2E4B] text-white rounded hover:bg-[#162440] flex items-center justify-center">
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Costs list */}
              {costs.length > 0 ? (
                <div className="border border-gray-200 rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Montant</th>
                        <th className="px-4 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.map((cost, index) => {
                        const typeInfo = COST_TYPES.find(t => t.value === cost.type) || { label: cost.type };
                        return (
                          <tr key={cost.id || index} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{typeInfo.label}</span>
                            </td>
                            <td className="px-4 py-2 text-gray-700">{cost.label}</td>
                            <td className="px-4 py-2 text-right font-semibold text-red-600">{cost.amount}€</td>
                            <td className="px-4 py-2 text-center">
                              <button type="button" onClick={() => removeCost(index)} className="text-gray-400 hover:text-red-500">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-100">
                      <tr>
                        <td colSpan={2} className="px-4 py-2 font-semibold text-gray-700">Total des charges</td>
                        <td className="px-4 py-2 text-right font-bold text-red-600">{totalCosts}€</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8 border border-dashed border-gray-200 rounded">
                  Aucune charge définie. Ajoutez les coûts moyens par prestation.
                </p>
              )}
            </div>
          )}

          {/* Tab: Photos */}
          {activeTab === "photos" && (
            <div className="border border-gray-200 rounded p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                  <ImageIcon size={14} /> Galerie photos ({images.length})
                </label>
                {vehicle?.id && (
                  <label className="flex items-center gap-2 px-3 py-1.5 bg-[#1B2E4B] text-white text-xs font-semibold rounded cursor-pointer hover:bg-[#162440] transition-colors">
                    <Upload size={12} />
                    {uploading ? "Upload..." : "Ajouter des photos"}
                    <input type="file" multiple accept="image/*" onChange={handleFileUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>
              
              {!vehicle?.id && (
                <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded">
                  Enregistrez d'abord le véhicule, puis ajoutez les photos.
                </p>
              )}

              {images.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <div key={img.id} className="relative group aspect-square bg-white rounded overflow-hidden border border-gray-200">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} 
                        alt={img.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        {index > 0 && (
                          <button type="button" onClick={() => moveImage(index, -1)}
                            className="p-1.5 bg-white rounded text-gray-700 hover:bg-gray-100" title="Déplacer à gauche">
                            ←
                          </button>
                        )}
                        {index < images.length - 1 && (
                          <button type="button" onClick={() => moveImage(index, 1)}
                            className="p-1.5 bg-white rounded text-gray-700 hover:bg-gray-100" title="Déplacer à droite">
                            →
                          </button>
                        )}
                        <button type="button" onClick={() => deleteImage(img.id)}
                          className="p-1.5 bg-red-500 rounded text-white hover:bg-red-600" title="Supprimer">
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {index === 0 && (
                        <span className="absolute top-1 left-1 text-[9px] bg-[#D4A843] text-white px-1.5 py-0.5 rounded font-semibold">
                          Principal
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : vehicle?.id ? (
                <p className="text-xs text-gray-400 text-center py-6">Aucune photo. Cliquez sur "Ajouter des photos" pour commencer.</p>
              ) : null}
            </div>
          )}
          
          <div className="flex gap-3 pt-2 justify-end border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50 rounded">Annuler</button>
            <button type="submit" disabled={loading} data-testid="save-vehicle-btn"
              className="px-6 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] disabled:opacity-60 transition-colors rounded">
              {loading ? "Sauvegarde..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
